import {
  FC,
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useMemo,
  useRef,
  useState,
  PropsWithChildren,
} from 'react';
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
  ApolloLink,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { setContext } from '@apollo/client/link/context';
import { Sentry } from 'lib/sentry';
import { SeverityLevel } from '@sentry/types';
import { sha256 } from 'crypto-hash';
import {
  useAccessToken,
  generateGraphQLHeaders,
  ClientHeadersByApp,
} from '@robinpowered/dashboard-apps-common';
import fetch from 'cross-fetch';
import { config } from '../config';
import { isUnauthenticatedPath } from '../constants/unauthPaths';
import { isUsingParentToken } from 'utils/isUsingParentToken';

type ApolloContextValue = {
  tenantId: string | undefined;
  setTenantId: Dispatch<SetStateAction<string | undefined>>;
};

const createHttpLinkWithUri = (uri: string) =>
  createHttpLink({ uri, fetch, credentials: 'include' });

let persistedQueryLink: ApolloLink | undefined;

try {
  // IE11 & Edge will not be able to use crypto, so only add persisted queries
  // where it's available.
  if (
    typeof window?.crypto?.subtle?.digest !== 'undefined' &&
    typeof window?.TextEncoder !== 'undefined'
  ) {
    /**
     * Persisted query link allows us to reduce the overhead
     * of repeated requests by sharing a request hash with the server.
     * This link also improves compatibility with CDN access by allowing
     * graphql over GET requests
     */
    persistedQueryLink = createPersistedQueryLink({
      sha256,
      useGETForHashedQueries: true,
      disable: (error) => {
        if (error.networkError) {
          return true;
        }
        return false;
      },
    });
  }
} catch (e) {} // eslint-disable-line no-empty

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, response }) => {
    const captureException = (
      message: string,
      level: SeverityLevel,
      extra?: Record<string, unknown>
    ) => {
      Sentry.withScope((scope) => {
        scope.setLevel(level);
        Sentry.captureException(message, {
          tags: {
            graphql: true,
          },
          extra: {
            ...extra,
            operation,
            response,
          },
        });
      });
    };

    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path, extensions }) => {
        if (
          config.env !== 'development' &&
          extensions?.code === 'UNAUTHENTICATED' &&
          !isUnauthenticatedPath(window.location.pathname) &&
          !isUsingParentToken()
        ) {
          const currentPath = window.location.pathname + window.location.search;
          const encodedPath = encodeURIComponent(currentPath);
          window.location.href = `${config.dashboardUrl}/login?redirectTo=${encodedPath}`;
          return;
        }

        captureException(`[GraphQL error]: Message: ${message}`, 'info', {
          path,
          locations,
        });
      });
    }
    if (networkError) {
      captureException(`[Network error]: ${networkError}`, 'info');
    }
  }
);

type DynamicAuthRefs = {
  accessToken: string | null;
  tenantId: string | undefined;
};

const createApolloClient = (
  authRefs: React.MutableRefObject<DynamicAuthRefs>,
  gqlUrl: string,
  appName: string,
  appVersion: string
) => {
  const authLink = setContext((_, prevContext) => ({
    // Merge into any per-operation headers (set via `useQuery({ context: { headers: ... } })`)
    // so callers can override specific values like `tenant-id` when needed.
    // generateGraphQLHeaders omits keys when their source value is null/undefined,
    // so this preserves the operation-provided values for those keys.
    headers: {
      ...prevContext.headers,
      ...generateGraphQLHeaders(
        authRefs.current.accessToken,
        authRefs.current.tenantId,
        ClientHeadersByApp.SHELL
      ),
    },
  }));

  const httpLink = createHttpLinkWithUri(gqlUrl);

  return new ApolloClient({
    clientAwareness: {
      name: appName,
      version: appVersion,
    },
    link: from([
      errorLink,
      authLink,
      ...(persistedQueryLink ? [persistedQueryLink] : []),
      httpLink,
    ]),
    cache: new InMemoryCache({
      typePolicies: {
        Organization: {
          fields: {
            features: {
              merge: true,
            },
          },
        },
        DeskSchedule: {
          keyFields: ['uniqueId'],
        },
      },
    }),
    resolvers: {},
  });
};

const ApolloContext = createContext<ApolloContextValue>({
  tenantId: undefined,
  setTenantId: () => null,
});

export const ApolloContextProvider: FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { gqlUrl, appName, appVersion, sessionCookieName } = config;
  const [tenantId, setTenantId] = useState<string | undefined>();
  const { accessToken } = useAccessToken(sessionCookieName);

  // Store auth values in a ref so the authLink reads the latest values
  // at request time without recreating the Apollo client (and its cache).
  const authRefs = useRef<DynamicAuthRefs>({ accessToken, tenantId });
  authRefs.current = { accessToken, tenantId };

  const apolloClient = useMemo(
    () => createApolloClient(authRefs, gqlUrl, appName, appVersion),
    [authRefs, gqlUrl, appName, appVersion]
  );

  return (
    <ApolloContext.Provider value={{ tenantId, setTenantId }}>
      <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
    </ApolloContext.Provider>
  );
};

export const useApolloContext = (): ApolloContextValue => {
  return useContext(ApolloContext);
};
